import InputInterface from '~/modules/GameEngine/Input/Interface';
import { MicInput } from '~/modules/GameEngine/Input/MicInput';
import { SelectedPlayerInput } from '~/modules/Players/PlayersManager';

const isDeviceSelectedForMultipleChannels = (allInputs: SelectedPlayerInput[] = [], deviceId: string | undefined) => {
  const playerInputs = allInputs.filter((input) => input.deviceId === deviceId).map((input) => input.channel);
  return playerInputs.some((channel) => playerInputs[0] !== channel);
};

// detecta ids “default”
const isDefaultId = (id?: string) => !id || id === 'default';

class MultiMicInput implements InputInterface {
  private devices: Record<string, InputInterface> = {};
  private defaultDeviceId: string | null = null;

  // evita carreras start/stop (especialmente con StrictMode)
  private generation = 0;

  private resolveId = (deviceId?: string) => {
    if (!deviceId || deviceId === 'default') return this.defaultDeviceId ?? 'default';
    return deviceId;
  };

  private ensureDeviceInstance = (id: string, allInputs?: SelectedPlayerInput[]) => {
    if (!this.devices[id]) {
      const multi = isDeviceSelectedForMultipleChannels(allInputs, id);
      this.devices[id] = multi ? new MicInput(2) : new MicInput(1);
    }
  };

  public startMonitoring = async (deviceId?: string, allInputs?: SelectedPlayerInput[]) => {
    const myGen = ++this.generation;

    if (deviceId && deviceId !== 'default') {
      this.defaultDeviceId = deviceId;
    }

    const id = this.resolveId(deviceId);

    this.ensureDeviceInstance(id, allInputs);

    // si un stop ocurre mientras estamos arrancando, abortamos al volver del await
    await this.devices[id].startMonitoring(id, allInputs);

    if (myGen !== this.generation) {
      // Se inició otro ciclo (stop/start) después; este start es obsoleto.
      // Importante: NO borres devices aquí. Solo deja que el ciclo actual mande.
      return;
    }
  };

  public stopMonitoring = async (deviceId?: string) => {
    // cualquier stop invalida starts en curso
    ++this.generation;

    if (deviceId !== undefined) {
      const id = this.resolveId(deviceId);
      const dev = this.devices[id];
      if (dev) {
        await dev.stopMonitoring(id);
        // NO borres la instancia si estás en dev con StrictMode o transiciones rápidas.
        // Deja la instancia creada para evitar que getFrequencies se quede sin nada.
        // delete this.devices[id];
      }
      if (this.defaultDeviceId === id) this.defaultDeviceId = null;
      return;
    }

    await Promise.all(Object.entries(this.devices).map(([id, dev]) => dev.stopMonitoring(id)));
    // Igual: no limpies el mapa, para que no exista “ventana” sin instancia.
    // this.devices = {};
    this.defaultDeviceId = null;
  };

  public getFrequencies = (deviceId?: string) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.getFrequencies(id) as number[] ?? [0, 0];
  };

  public getVolumes = (deviceId?: string) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.getVolumes(id) ?? [0, 0];
  };

  public clearFrequencies = (deviceId?: string) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.clearFrequencies(id);
  };

  public getInputLag = (deviceId?: string) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.getInputLag(id) ?? 180;
  };

  public requestReadiness = async (deviceId?: string) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.requestReadiness(id) ?? true;
  };

  public getStatus = (deviceId?: string, channel?: number) => {
    const id = this.resolveId(deviceId);
    return this.devices[id]?.getStatus(id, channel) ?? ('ok' as const);
  };
}


export default new MultiMicInput();
